import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Picture, Text, Button } from 'components-guide';
import { CrosswordForm } from 'forms';
import { useHubspotForm } from 'hooks';
import Config from 'config';

import './Crossword.scss';

const { isDev } = Config;

export interface CrosswordProps {
    /**
     * Crossword id
     */
    id: string;
    /**
     * Crossword image
     */
    image: string;
    /**
     * Crossword
     */
    crossword: any;
    /**
     * Horizontal clues
     */
    horizontal: string[];
    /**
     * Vertical clues
     */
    vertical: string[];
    /**
     * Date until form will be visible - format  ISO 8601 "YYYY-MM-DD"
     */
    dueDate?: string;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Style
     */
    style?: any;
}

const Crossword: React.FC<CrosswordProps> = ({
    id,
    image,
    crossword,
    horizontal = [],
    vertical = [],
    dueDate,
    className,
    style,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showEmptyError, setShowEmptyError] = useState(false);
    const { onSubmit } = useHubspotForm({
        analytics: { data_traffic_origin: 'Web_Blog' },
        t,
        options: {
            successMessage: {
                title: t('components.crossword.successMessage.title'),
                message: t('components.crossword.successMessage.message'),
            },
        },
    });
    const classNames = classnames(['crossword', className]);

    useEffect(() => {
        // Fixes maxLength not working on Android phones
        const inputs = document.querySelectorAll('.char');

        (inputs || []).forEach((input: any) => {
            /* eslint-disable no-param-reassign */
            input.onkeyup = function (e) {
                if (input.value.length > 1) {
                    input.value = input.value.substring(0, 1);
                }
            };
            /* eslint-enable no-param-reassign */
        });

        if (dueDate) {
            const showForm = new Date(dueDate).getTime() > new Date().getTime();
            setShowForm(showForm);
        }
    }, []);

    const mockCrossword = () => {
        const crosswordFields = document.querySelectorAll('[data-letter]');
        crosswordFields.forEach(field => field.setAttribute('value', field.getAttribute('data-letter') || ''));
    };

    const normalizeLetter = (letter) => letter.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    const validateCrossword = solution => {
        let empty = false;
        let valid = true;
        for (let i = 0; i < solution.length; i++) {
            if (solution[i].value === '') {
                empty = true;
            }
            if (normalizeLetter(solution[i].value.toUpperCase()) !== normalizeLetter(solution[i].getAttribute('data-letter'))) {
                valid = false;
            }
        }

        return { empty, valid };
    };

    const handleSubmit = async (form: any) => {
        setLoading(true);
        const { privacyCrossword: privacy, newsletterCrossword: newsletter } = form;
        const solution = document.querySelectorAll('[data-letter]');
        const { valid, empty } = validateCrossword(solution);
        setShowEmptyError(empty);
        if (!empty) {
            onSubmit({ ...form, privacy, newsletter, crosswordId: id, crosswordValid: valid });
        } else {
            document.getElementById('crossword')?.scrollIntoView({ behavior: 'smooth' });
        }
        setLoading(false);
    };

    return (
        <div className={classNames} style={style}>
            <div className="crossword__image flex row-center">
                <Picture src={image} alt={t('components.crossword.misteriousCharacter')} width={350} />
            </div>

            {crossword}
            {showEmptyError ? (
                <Text typo="span" align="center" color="error" id="crossword__error" className="crossword__error">
                    {t('components.crossword.empty')}
                </Text>
            ) : null}
            {isDev ? (
                <div className="flex row-center">
                    <Button onClick={mockCrossword}>Mock</Button>
                </div>
            ) : null}

            <div className="crossword__clues">
                <div className="crossword__clues--horizontal">
                    <Text weight="bold" className="ma-0 pa-0">
                        {t('components.crossword.horizontal')}
                    </Text>
                    {horizontal.map((clue, index) => (
                        <Text className="ma-0 pa-0" key={`clue-horizontal-${index}`}>
                            {clue}
                        </Text>
                    ))}
                </div>

                <div className="crossword__clues--vertical mt-2">
                    <Text weight="bold" className="ma-0 pa-0">
                        {t('components.crossword.vertical')}
                    </Text>
                    {vertical.map((clue, index) => (
                        <Text className="ma-0 pa-0" key={`clue-vertical-${index}`}>
                            {clue}
                        </Text>
                    ))}
                </div>
            </div>

            {showForm ? (
                <div className="crossword__form mt-4">
                    <CrosswordForm loading={loading} onSubmit={handleSubmit} t={t} />
                </div>
            ) : null}
        </div>
    );
};

export default Crossword;
